<template>
    <div class="editor-page" :style="pageCssVars">
        <div class="page-header">
            <div class="header-left">
                <Button type="text" @click="goBack" style="margin-right: 8px;">
                    <Icon type="ios-arrow-back" size="20" />
                    返回
                </Button>
                <span class="header-title">在线合同编辑器</span>
            </div>
            <div class="header-right">
                <Upload ref="docxUpload" :before-upload="handleDocxUpload" :show-upload-list="false" :action="''" accept=".docx"
                    style="display: inline-block; margin-right: 12px;">
                    <Button type="default" icon="ios-cloud-upload-outline">
                        导入 DOCX
                    </Button>
                </Upload>
                <Button type="default" icon="ios-download-outline" @click="exportToDocx" style="margin-right: 12px;">
                    导出 DOCX
                </Button>
                <Button type="default" icon="ios-print-outline" @click="printDocument" style="margin-right: 12px;">
                    打印/PDF
                </Button>
                <Button type="primary" icon="md-checkmark" @click="saveAndBack" style="margin-right: 12px;">
                    保存并返回
                </Button>
                <Button :type="isEditMode ? 'default' : 'primary'" @click="toggleEditMode">
                    {{ isEditMode ? '切换到预览' : '切换到编辑' }}
                </Button>
            </div>
        </div>

        <div class="editor-container">
            <!-- 工具栏 -->
            <div class="editor-toolbar v2" v-if="isEditMode && showCustomToolbar && editor">
                <div class="ribbon">
                    <!-- 导入 -->
                    <div class="ribbon-section">
                        <div class="section-main">
                            <div class="big-btn-col">
                                <Tooltip content="导入 DOCX" transfer>
                                    <Button class="big-btn" @click="triggerDocxSelect">
                                        <Icon type="md-log-in" />
                                        <span class="big-btn-text">导入</span>
                                    </Button>
                                </Tooltip>
                                <div class="small-row">
                                    <Tooltip content="撤销（Ctrl+Z）" transfer>
                                        <Button size="small" @click="editor.chain().focus().undo().run()" :disabled="!editor.can().undo()">
                                            <Icon type="ios-undo" />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content="重做（Ctrl+Y）" transfer>
                                        <Button size="small" @click="editor.chain().focus().redo().run()" :disabled="!editor.can().redo()">
                                            <Icon type="ios-redo" />
                                        </Button>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                        <div class="section-title">导入</div>
                    </div>

                    <div class="ribbon-divider"></div>

                    <!-- 字体 -->
                    <div class="ribbon-section wide">
                        <div class="section-main">
                            <Select v-model="headerLevel" size="small" style="width: 110px;" @on-change="setHeader">
                                <Option value="">正文</Option>
                                <Option :value="1">标题1</Option>
                                <Option :value="2">标题2</Option>
                                <Option :value="3">标题3</Option>
                                <Option :value="4">标题4</Option>
                            </Select>
                            <Select v-model="fontFamily" size="small" style="width: 130px;" @on-change="setFontFamily">
                                <Option value="Arial">Arial</Option>
                                <Option value="Times New Roman">Times New Roman</Option>
                                <Option value="SimSun">宋体</Option>
                                <Option value="SimHei">黑体</Option>
                                <Option value="KaiTi">楷体</Option>
                                <Option value="Microsoft YaHei">微软雅黑</Option>
                            </Select>
                            <Select v-model="fontSize" size="small" style="width: 74px;" @on-change="setFontSize">
                                <Option :value="12">12</Option>
                                <Option :value="14">14</Option>
                                <Option :value="16">16</Option>
                                <Option :value="18">18</Option>
                                <Option :value="20">20</Option>
                                <Option :value="24">24</Option>
                                <Option :value="28">28</Option>
                                <Option :value="32">32</Option>
                                <Option :value="36">36</Option>
                            </Select>
                            <div class="btn-row">
                                <Tooltip content="粗体（Ctrl+B）" transfer>
                                    <Button size="small" @click="editor.chain().focus().toggleBold().run()"
                                        :class="{ 'is-active': editor.isActive('bold') }">
                                        <strong>B</strong>
                                    </Button>
                                </Tooltip>
                                <Tooltip content="斜体（Ctrl+I）" transfer>
                                    <Button size="small" @click="editor.chain().focus().toggleItalic().run()"
                                        :class="{ 'is-active': editor.isActive('italic') }">
                                        <em>I</em>
                                    </Button>
                                </Tooltip>
                                <Tooltip content="下划线（Ctrl+U）" transfer>
                                    <Button size="small" @click="editor.chain().focus().toggleUnderline().run()"
                                        :class="{ 'is-active': editor.isActive('underline') }">
                                        <u>U</u>
                                    </Button>
                                </Tooltip>
                                <Tooltip content="删除线（Ctrl+Shift+S）" transfer>
                                    <Button size="small" @click="editor.chain().focus().toggleStrike().run()"
                                        :class="{ 'is-active': editor.isActive('strike') }">
                                        <s>S</s>
                                    </Button>
                                </Tooltip>
                                <ColorPicker v-model="textColor" size="small" @on-change="setTextColor"
                                    :transfer="true" :colors="textColorOptions">
                                    <Tooltip content="文字颜色" transfer>
                                        <Button size="small"><span class="color-chip" :style="{ color: textColor }">A</span></Button>
                                    </Tooltip>
                                </ColorPicker>
                                <ColorPicker v-model="backgroundColor" size="small" @on-change="setBackgroundColor"
                                    :transfer="true" :colors="backgroundColorOptions">
                                    <Tooltip content="背景颜色" transfer>
                                        <Button size="small"><span class="bg-chip" :style="{ backgroundColor: backgroundColor }"></span></Button>
                                    </Tooltip>
                                </ColorPicker>
                            </div>
                        </div>
                        <div class="section-title">字体</div>
                    </div>

                    <div class="ribbon-divider"></div>

                    <!-- 段落 -->
                    <div class="ribbon-section">
                        <div class="section-main">
                            <div class="btn-row">
                                <Tooltip content="左对齐（Ctrl+Shift+L）" transfer>
                                    <Button size="small" @click="editor.chain().focus().setTextAlign('left').run()"
                                        :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }">
                                        <Icon type="md-return-left" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="居中（Ctrl+Shift+E）" transfer>
                                    <Button size="small" @click="editor.chain().focus().setTextAlign('center').run()"
                                        :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }">
                                        <Icon type="md-swap" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="右对齐（Ctrl+Shift+R）" transfer>
                                    <Button size="small" @click="editor.chain().focus().setTextAlign('right').run()"
                                        :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }">
                                        <Icon type="md-return-right" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="两端对齐（Ctrl+Shift+J）" transfer>
                                    <Button class="with-caret" size="small" @click="editor.chain().focus().setTextAlign('justify').run()"
                                        :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }">
                                        <Icon type="md-menu" />
                                        <Icon class="caret" type="md-arrow-dropdown" />
                                    </Button>
                                </Tooltip>
                            </div>
                            <div class="btn-row">
                                <Tooltip content="无序列表（Ctrl+Shift+8）" transfer>
                                    <Button size="small" @click="editor.chain().focus().toggleBulletList().run()"
                                        :class="{ 'is-active': editor.isActive('bulletList') }">
                                        <Icon type="ios-list" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="有序列表（Ctrl+Shift+7）" transfer>
                                    <Button size="small" @click="editor.chain().focus().toggleOrderedList().run()"
                                        :class="{ 'is-active': editor.isActive('orderedList') }">
                                        1.
                                    </Button>
                                </Tooltip>
                                <Tooltip content="引用（Ctrl+Shift+B）" transfer>
                                    <Button size="small" @click="editor.chain().focus().toggleBlockquote().run()"
                                        :class="{ 'is-active': editor.isActive('blockquote') }">
                                        “”
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                        <div class="section-title">段落</div>
                    </div>

                    <div class="ribbon-divider"></div>

                    <!-- 页面 -->
                    <div class="ribbon-section">
                        <div class="section-main">
                            <Select v-model="pagePreset" size="small" style="width: 120px;" @on-change="applyPagePreset">
                                <Option value="A4">A4 (800×1132)</Option>
                                <Option value="A5">A5 (565×800)</Option>
                            </Select>
                            <Tooltip content="纵向" transfer>
                                <Button size="small" :class="{ 'is-active': orientation === 'portrait' }" @click="setOrientation('portrait')">
                                    <Icon type="md-paper" />
                                </Button>
                            </Tooltip>
                            <Tooltip content="横向" transfer>
                                <Button size="small" :class="{ 'is-active': orientation === 'landscape' }" @click="setOrientation('landscape')">
                                    <Icon type="md-browsers" />
                                </Button>
                            </Tooltip>
                            <Select v-model="marginPreset" size="small" style="width: 110px;" @on-change="applyMarginPreset">
                                <Option value="narrow">窄边距</Option>
                                <Option value="normal">普通</Option>
                                <Option value="wide">宽边距</Option>
                            </Select>
                        </div>
                        <div class="section-title">页面</div>
                    </div>

                    <div class="ribbon-divider"></div>

                    <!-- 插入 -->
                    <div class="ribbon-section">
                        <div class="section-main">
                            <div class="btn-row">
                                <Tooltip content="图片" transfer>
                                    <Button size="small" @click="showImageModal = true">
                                        <Icon type="md-image" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="表格" transfer>
                                    <Button size="small" @click="insertTable">表格</Button>
                                </Tooltip>
                                <Tooltip content="分割线" transfer>
                                    <Button size="small" @click="insertHorizontalRule">—</Button>
                                </Tooltip>
                                <Tooltip content="分页符" transfer>
                                    <Button size="small" @click="insertPageBreak">分页</Button>
                                </Tooltip>
                            </div>
                            <div class="btn-row">
                                <Tooltip content="插入链接（Ctrl+K）" transfer>
                                    <Button size="small" @click="insertLink">
                                        <Icon type="md-link" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="清除格式（Ctrl+\\）" transfer>
                                    <Button size="small" @click="clearFormatting">
                                        <Icon type="md-trash" />
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                        <div class="section-title">插入</div>
                    </div>

                    <div class="ribbon-spacer"></div>

                    <!-- 查找 -->
                    <div class="ribbon-section">
                        <div class="section-main">
                            <div class="find-box">
                                <Input
                                    v-model="findQuery"
                                    size="small"
                                    placeholder="查找"
                                    style="width: 140px"
                                    @on-change="onFindChange"
                                    @on-enter="findNext"
                                />
                                <Tooltip content="上一个（Shift+Enter）" transfer>
                                    <Button size="small" @click="findPrev" :disabled="!canFindMove">
                                        <Icon type="md-arrow-round-up" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="下一个（Enter）" transfer>
                                    <Button size="small" @click="findNext" :disabled="!canFindMove">
                                        <Icon type="md-arrow-round-down" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="清空" transfer>
                                    <Button size="small" @click="clearFind" :disabled="!findQuery">
                                        <Icon type="md-close" />
                                    </Button>
                                </Tooltip>
                                <span class="find-count" v-if="findQuery">{{ findIndexLabel }}</span>
                            </div>
                        </div>
                        <div class="section-title">查找</div>
                    </div>

                    <div class="ribbon-divider"></div>

                    <!-- 操作 -->
                    <div class="ribbon-section">
                        <div class="section-main">
                            <Tooltip :content="`字数：${wordCount}`" transfer>
                                <div class="word-pill">{{ wordCount }} 字</div>
                            </Tooltip>
                            <Button type="primary" size="small" @click="saveContent">保存</Button>
                        </div>
                        <div class="section-title">操作</div>
                    </div>
                </div>
            </div>

            <!-- 编辑器主体 -->
            <div class="editor-content">
                <!-- 编辑模式：显示可编辑的编辑器（带分页效果） -->
                <div class="editor-mode" v-if="isEditMode">
                    <div class="pages-container">
                        <!-- Tiptap Floating Menu (原生工具栏) -->
                        <floating-menu
                            v-if="editor"
                            :editor="editor"
                            :tippy-options="{ duration: 100 }"
                            class="tiptap-floating-menu"
                        >
                            <button
                                @click="editor.chain().focus().toggleBold().run()"
                                :class="{ 'is-active': editor.isActive('bold') }"
                                title="粗体"
                            >
                                <strong>B</strong>
                            </button>
                            <button
                                @click="editor.chain().focus().toggleItalic().run()"
                                :class="{ 'is-active': editor.isActive('italic') }"
                                title="斜体"
                            >
                                <em>I</em>
                            </button>
                            <button
                                @click="editor.chain().focus().toggleStrike().run()"
                                :class="{ 'is-active': editor.isActive('strike') }"
                                title="删除线"
                            >
                                <s>S</s>
                            </button>
                            <button
                                @click="editor.chain().focus().toggleCode().run()"
                                :class="{ 'is-active': editor.isActive('code') }"
                                title="行内代码"
                            >
                                &lt;/&gt;
                            </button>
                            <span class="divider"></span>
                            <button
                                @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                                :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
                                title="标题 1"
                            >
                                H1
                            </button>
                            <button
                                @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                                :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                                title="标题 2"
                            >
                                H2
                            </button>
                            <button
                                @click="editor.chain().focus().toggleBulletList().run()"
                                :class="{ 'is-active': editor.isActive('bulletList') }"
                                title="无序列表"
                            >
                                •
                            </button>
                            <button
                                @click="editor.chain().focus().toggleOrderedList().run()"
                                :class="{ 'is-active': editor.isActive('orderedList') }"
                                title="有序列表"
                            >
                                1.
                            </button>
                            <button
                                @click="editor.chain().focus().toggleBlockquote().run()"
                                :class="{ 'is-active': editor.isActive('blockquote') }"
                                title="引用"
                            >
                                "
                            </button>
                        </floating-menu>

                        <!-- 动态计算并显示编辑器页面 -->
                        <div class="page-editor-wrapper" ref="editorWrapper">
                            <editor-content v-if="editor" :editor="editor" class="editor-wrapper" />
                        </div>

                        <!-- 页面计数器 -->
                        <div class="page-counter">
                            <div class="counter-content">
                                <Icon type="md-document" />
                                <span class="counter-text">纸张尺寸: {{ pageWidth }}px × {{ pageHeight }}px</span>
                                <span class="divider">|</span>
                                <span class="counter-text" v-if="totalPages > 1">共 {{ totalPages }} 页</span>
                                <span class="counter-text" v-else>单页编辑</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 预览模式：显示分页后的多页内容 -->
                <div class="preview-mode" v-else>
                    <div class="pages-container">
                        <div v-for="(page, index) in pages" :key="index" class="page"
                            :class="{ 'active-page': currentPage === index + 1 }" @click="currentPage = index + 1">
                            <div class="page-number">第 {{ index + 1 }} 页</div>
                            <div class="page-content" v-html="page.content"></div>
                        </div>

                        <!-- 空白页占位（让用户知道可以继续编辑） -->
                        <div class="page-placeholder" v-if="pages.length === 1">
                            <div class="placeholder-text">
                                <Icon type="md-document" size="48" />
                                <p>继续编辑内容...</p>
                            </div>
                        </div>

                        <!-- 页面计数器 -->
                        <div class="page-counter" v-if="totalPages > 0">
                            <div class="counter-content">
                                <Icon type="md-document" />
                                <span class="counter-text">纸张尺寸: {{ pageWidth }}px × {{ pageHeight }}px</span>
                                <span class="divider">|</span>
                                <span class="counter-text">共 {{ totalPages }} 页</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 表格设置对话框 -->
        <Modal v-model="showTableModal" title="插入表格" @on-ok="confirmInsertTable">
            <Form :label-width="80">
                <FormItem label="行数">
                    <InputNumber v-model="tableRows" :min="1" :max="20" />
                </FormItem>
                <FormItem label="列数">
                    <InputNumber v-model="tableCols" :min="1" :max="10" />
                </FormItem>
            </Form>
        </Modal>

        <!-- 图片上传对话框 -->
        <Modal v-model="showImageModal" title="插入图片" @on-ok="confirmInsertImage">
            <Upload :before-upload="handleImageUpload" :show-upload-list="false" :action="''" accept="image/*">
                <Button type="primary" icon="ios-cloud-upload-outline">选择图片</Button>
            </Upload>
            <div v-if="tempImageUrl" style="margin-top: 12px;">
                <img :src="tempImageUrl" style="max-width: 100%; max-height: 200px;" />
            </div>
        </Modal>
    </div>
</template>

<script>
import { Editor, EditorContent, FloatingMenu } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import TextStyle from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'
import Image from '@tiptap/extension-image'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import FloatingMenuExtension from '@tiptap/extension-floating-menu'

import mammoth from 'mammoth'
import { saveAs } from 'file-saver'
import htmlDocx from 'html-docx-js/dist/html-docx.js'

export default {
    name: 'EditorPage',
    components: {
        EditorContent,
        FloatingMenu
    },
    computed: {
        pageCssVars() {
            return {
                '--page-width': `${this.pageWidth}px`,
                '--page-height': `${this.pageHeight}px`,
                '--page-padding': `${this.pagePadding}px`
            }
        },
        canFindMove() {
            return !!this.editor && !!this.findQuery && this.findMatches.length > 0
        },
        findIndexLabel() {
            if (!this.findQuery) return ''
            if (!this.findMatches.length) return '0/0'
            const idx = this.findMatchIndex >= 0 ? this.findMatchIndex + 1 : 1
            return `${idx}/${this.findMatches.length}`
        }
    },
    data() {
        return {
            editor: null,
            editorReady: false, // 编辑器是否准备就绪
            showCustomToolbar: true, // 是否显示自定义工具栏
            headerLevel: '',
            fontFamily: 'SimSun',
            fontSize: 16,
            textColor: '#000000',
            backgroundColor: '#ffffff',
            wordCount: 0,
            textColorOptions: [
                '#000000', '#515a6e', '#2d8cf0', '#19be6b',
                '#ff9900', '#ed4014', '#9c27b0', '#e91e63',
                '#000080', '#008000', '#800000', '#808000'
            ],
            backgroundColorOptions: [
                '#ffffff', '#f8f8f9', '#e8eaed', '#dcdcdc',
                '#fff7e6', '#fff0f0', '#f6ffed', '#f0f5ff',
                '#e6f7ff', '#fffbe6', '#fff1f0', '#f9f0ff'
            ],
            imageUrl: 'https://via.placeholder.com/300',
            showTableModal: false,
            tableRows: 3,
            tableCols: 3,
            showImageModal: false,
            tempImageUrl: '',
            // 查找
            findQuery: '',
            findMatches: [],
            findMatchIndex: -1,
            initialContent: '',
            currentPage: 1,
            totalPages: 1,
            pages: [{ content: '' }], // 页面数组
            pageHeight: 1132, // A4 高度
            pageWidth: 800, // A4 宽度
            pagePadding: 60, // 页面内边距
            contentHeight: 1012, // 实际内容高度 = 1132 - 60*2
            pagePreset: 'A4',
            orientation: 'portrait',
            marginPreset: 'normal',
            isEditMode: true, // 编辑模式 / 预览模式
            editorBackgroundImage: '' // 编辑器动态背景
        }
    },
    mounted() {
        this.initEditor()
        // 从路由参数获取初始内容
        if (this.$route.query.content) {
            this.editor.commands.setContent(this.$route.query.content)
        }
        // 初始化后执行一次分页计算
        this.$nextTick(() => {
            setTimeout(() => {
                this.updateEditorPageBreaks()
            }, 500)
        })
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy()
        }
    },
    methods: {
        triggerDocxSelect() {
            const upload = this.$refs.docxUpload
            if (!upload || !upload.$el) {
                this.$Message.warning('上传组件未就绪')
                return
            }
            const input = upload.$el.querySelector('input[type="file"]')
            if (input) {
                input.click()
            } else {
                this.$Message.warning('未找到文件选择器')
            }
        },

        applyPagePreset(preset) {
            // preset 来自 Select 的 v-model 或回调参数
            const val = preset || this.pagePreset
            this.pagePreset = val
            const presets = {
                A4: { w: 800, h: 1132 },
                A5: { w: 565, h: 800 }
            }
            const base = presets[val] || presets.A4

            if (this.orientation === 'landscape') {
                this.pageWidth = base.h
                this.pageHeight = base.w
            } else {
                this.pageWidth = base.w
                this.pageHeight = base.h
            }

            this.contentHeight = this.pageHeight - this.pagePadding * 2
            this.$nextTick(() => {
                if (!this.isEditMode) this.checkPageBreak()
            })
        },

        setOrientation(ori) {
            if (ori !== 'portrait' && ori !== 'landscape') return
            if (this.orientation === ori) return
            this.orientation = ori
            // 重新应用当前 preset
            this.applyPagePreset(this.pagePreset)
        },

        applyMarginPreset(preset) {
            const val = preset || this.marginPreset
            this.marginPreset = val
            const map = {
                narrow: 40,
                normal: 60,
                wide: 80
            }
            this.pagePadding = map[val] || 60
            this.contentHeight = this.pageHeight - this.pagePadding * 2
            this.$nextTick(() => {
                if (!this.isEditMode) this.checkPageBreak()
            })
        },

        onFindChange() {
            this.updateFindMatches(this.findQuery)
            if (this.findMatches.length) {
                this.findMatchIndex = 0
                this.scrollToMatch(this.findMatches[0])
            } else {
                this.findMatchIndex = -1
            }
        },

        clearFind() {
            this.findQuery = ''
            this.findMatches = []
            this.findMatchIndex = -1
            if (this.editor) {
                this.editor.commands.focus()
            }
        },

        findNext() {
            if (!this.canFindMove) return
            this.findMatchIndex = (this.findMatchIndex + 1) % this.findMatches.length
            this.scrollToMatch(this.findMatches[this.findMatchIndex])
        },

        findPrev() {
            if (!this.canFindMove) return
            this.findMatchIndex = (this.findMatchIndex - 1 + this.findMatches.length) % this.findMatches.length
            this.scrollToMatch(this.findMatches[this.findMatchIndex])
        },

        updateFindMatches(query) {
            if (!this.editor) return
            const q = (query || '').trim()
            if (!q) {
                this.findMatches = []
                return
            }

            const matches = []
            const doc = this.editor.state.doc
            doc.descendants((node, pos) => {
                if (!node.isText || !node.text) return
                const text = node.text
                let fromIndex = 0
                let idx = text.indexOf(q, fromIndex)
                while (idx !== -1) {
                    const from = pos + idx
                    const to = from + q.length
                    matches.push({ from, to })
                    fromIndex = idx + Math.max(1, q.length)
                    idx = text.indexOf(q, fromIndex)
                }
            })

            this.findMatches = matches
        },

        scrollToMatch(match) {
            if (!this.editor || !match) return
            const { from, to } = match
            if (this.editor.commands.setTextSelection) {
                this.editor.commands.setTextSelection({ from, to })
                this.editor.commands.focus()
                return
            }
            // 兜底：至少把光标移动过去
            this.editor.commands.focus()
            if (this.editor.commands.setTextSelection) {
                this.editor.commands.setTextSelection(from)
            }
        },

        initEditor() {
            console.log('Initializing Tiptap editor...')
            this.editor = new Editor({
                content: this.initialContent || '<p>开始编辑您的合同内容...</p>',
                extensions: [
                    StarterKit,
                    FloatingMenuExtension.configure({
                        element: document.querySelector('.tiptap-floating-menu')
                    }),
                    TextAlign.configure({
                        types: ['heading', 'paragraph']
                    }),
                    Underline,
                    TextStyle,
                    FontFamily,
                    Image.configure({
                        inline: true,
                        allowBase64: true
                    }),
                    HorizontalRule,
                    Table.configure({
                        resizable: true
                    }),
                    TableRow,
                    TableHeader,
                    TableCell
                ],
                onCreate: () => {
                    console.log('Editor created successfully')
                    this.editorReady = true
                },
                onUpdate: () => {
                    // 更新字数统计
                    this.updateWordCount()
                    // 编辑模式下监听内容变化，更新分页视觉效果
                    this.$nextTick(() => {
                        this.updateEditorPageBreaks()
                    })
                }
            })
        },

        setHeader(level) {
            if (!level) {
                this.editor.chain().focus().setParagraph().run()
            } else {
                this.editor.chain().focus().toggleHeading({ level }).run()
            }
        },

        setFontFamily(font) {
            this.editor.chain().focus().setFontFamily(font).run()
        },

        setFontSize(size) {
            // TipTap 原生不支持字号，需要使用 mark 的方式
            const { from, to } = this.editor.state.selection
            if (from === to) {
                // 没有选中文本时，设置默认字号
                this.fontSize = size
            } else {
                // 有选中文本时，应用字号样式
                const tr = this.editor.state.tr
                this.editor.view.dispatch(tr.addMark(from, to, this.editor.schema.marks.textStyle.create({ fontSize: `${size}px` })))
            }
        },

        setTextColor(color) {
            this.editor.chain().focus().setMark('textStyle', { color }).run()
        },

        setBackgroundColor(color) {
            this.editor.chain().focus().setMark('textStyle', { backgroundColor: color }).run()
        },

        updateWordCount() {
            if (this.editor) {
                const text = this.editor.getText()
                this.wordCount = text.length
            }
        },

        insertLink() {
            const url = prompt('请输入链接地址:', 'https://')
            if (url) {
                this.editor.chain().focus().setLink({ href: url }).run()
            }
        },

        insertHorizontalRule() {
            this.editor.chain().focus().setHorizontalRule().run()
        },

        insertTable() {
            this.showTableModal = true
        },

        confirmInsertTable() {
            this.editor.chain().focus()
                .insertTable({ rows: this.tableRows, cols: this.tableCols, withHeaderRow: true })
                .run()
            this.showTableModal = false
        },

        clearFormatting() {
            this.editor.chain().focus().unsetAllMarks().run()
            this.editor.chain().focus().clearNodes().run()
        },

        async handleImageUpload(file) {
            // iView Upload：配合 before-upload，返回 false 可阻止真实上传请求
            try {
                const reader = new FileReader()
                reader.onload = () => {
                    this.tempImageUrl = reader.result
                }
                reader.readAsDataURL(file)
            } catch (e) {
                console.error('handleImageUpload error:', e)
                this.$Message.error('图片读取失败')
            }
            return false
        },

        confirmInsertImage() {
            if (!this.tempImageUrl) {
                this.$Message.warning('请先选择图片')
                return
            }

            try {
                // Tiptap Image extension 通常支持 setImage / insertImage
                this.editor.chain().focus().insertImage(this.tempImageUrl).run()
            } catch (e) {
                // 兜底：如果 insertImage 不存在，尝试 setImage
                try {
                    this.editor.commands.setImage({ src: this.tempImageUrl })
                } catch (err) {
                    console.error('插入图片失败:', err)
                    this.$Message.error('插入图片失败')
                    return
                }
            }

            this.showImageModal = false
            this.tempImageUrl = ''

            this.$nextTick(() => {
                this.updateEditorPageBreaks()
            })
        },

        // 修正 mammoth DOCX -> HTML 的一部分样式差异，让 tiptap 更容易解析下划线/背景色
        normalizeImportedHtmlForTiptap(html) {
            if (!html) return ''
            try {
                const parser = new DOMParser()
                const doc = parser.parseFromString(html, 'text/html')

                const spans = Array.from(doc.querySelectorAll('span'))
                spans.forEach(span => {
                    const styleAttr = (span.getAttribute('style') || '')
                    const styleLower = styleAttr.toLowerCase()

                    // 下划线：mammoth 常见为 text-decoration: underline
                    if (styleLower.includes('text-decoration') && styleLower.includes('underline')) {
                        const u = doc.createElement('u')

                        const cleanedStyle = styleAttr.replace(/text-decoration\s*:\s*underline;?/ig, '').trim()
                        if (cleanedStyle) u.setAttribute('style', cleanedStyle)
                        if (span.getAttribute('class')) u.setAttribute('class', span.getAttribute('class'))

                        u.innerHTML = span.innerHTML
                        span.replaceWith(u)
                        return
                    }

                    // 背景色：有时 mammoth 使用 background 而不是 background-color
                    if (!styleLower.includes('background-color') && styleLower.includes('background')) {
                        const match = /background\s*:\s*([^;]+);?/i.exec(styleAttr)
                        if (match && match[1]) {
                            span.style.backgroundColor = match[1].trim()
                        }
                    }
                })

                // tiptap 通常更适合接收 body 内的片段
                return doc.body.innerHTML
            } catch (err) {
                console.warn('normalizeImportedHtmlForTiptap error:', err)
                return html
            }
        },

        async handleDocxUpload(file) {
            const reader = new FileReader()
            reader.onload = async (e) => {
                try {
                    const arrayBuffer = e.target.result
                    const result = await mammoth.convertToHtml({ arrayBuffer })
                    const normalizedHtml = this.normalizeImportedHtmlForTiptap(result.value)
                    this.editor.commands.setContent(normalizedHtml)

                    // 导入后触发分页
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.updateEditorPageBreaks()
                        }, 300)
                    })

                    this.$Message.success('DOCX 文件导入成功')
                } catch (error) {
                    console.error('导入失败:', error)
                    this.$Message.error('DOCX 文件导入失败: ' + error.message)
                }
            }
            reader.readAsArrayBuffer(file)
            return false
        },

        async exportToDocx() {
            try {
                // 使用编辑器的完整内容
                const htmlContent = this.editor.getHTML()

                const fullHtml = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="utf-8">
                        <style>
                            body {
                                font-family: "SimSun", "宋体", serif;
                                font-size: 16px;
                                line-height: 1.8;
                            }
                            u { text-decoration: underline; }
                            strong, b { font-weight: bold; }
                            em, i { font-style: italic; }
                            p { margin: 8px 0; text-indent: 2em; }
                            table { border-collapse: collapse; width: 100%; }
                            table, th, td { border: 1px solid #000; padding: 8px; }
                        </style>
                    </head>
                    <body>
                        ${htmlContent}
                    </body>
                    </html>
                `

                const converted = htmlDocx.asBlob(fullHtml)
                saveAs(converted, '合同文档.docx')
                this.$Message.success('DOCX 导出成功')
            } catch (error) {
                console.error('导出失败:', error)
                this.$Message.error('文档导出失败: ' + error.message)
            }
        },

        printDocument() {
            // 获取编辑器内容
            const htmlContent = this.editor.getHTML()

            // 创建新窗口用于打印
            const printWindow = window.open('', '_blank')
            const printDocument = printWindow.document

            // 写入打印内容
            printDocument.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <title>合同文档</title>
                    <style>
                        @page {
                            size: A4;
                            margin: 20mm;
                        }
                        body {
                            font-family: "SimSun", "宋体", serif;
                            font-size: 14px;
                            line-height: 1.8;
                            margin: 0;
                            padding: 0;
                        }

                        /* 分页符样式 */
                        .page-break-placeholder {
                            page-break-after: always !important;
                            break-after: page !important;
                            display: none !important;
                            height: 0 !important;
                            border: none !important;
                            margin: 0 !important;
                        }

                        /* 标题避免分页 */
                        h1, h2, h3, h4, h5, h6 {
                            page-break-after: avoid;
                            break-after: avoid;
                        }

                        /* 表格避免分页 */
                        table {
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }

                        /* 图片避免分页 */
                        img {
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }
                        h1, h2, h3, h4, h5, h6 {
                            font-weight: bold;
                            margin-top: 1.5em;
                            margin-bottom: 0.5em;
                            line-height: 1.25;
                        }
                        h1 { font-size: 28px; }
                        h2 { font-size: 24px; }
                        h3 { font-size: 20px; }
                        h4 { font-size: 18px; }
                        h5 { font-size: 16px; }
                        h6 { font-size: 14px; }
                        p {
                            margin: 8px 0;
                            text-align: justify;
                        }
                        strong, b {
                            font-weight: bold;
                        }
                        em, i {
                            font-style: italic;
                        }
                        u {
                            text-decoration: underline;
                        }
                        s {
                            text-decoration: line-through;
                        }
                        ul, ol {
                            padding-left: 1.5em;
                            margin: 8px 0;
                        }
                        li {
                            margin: 4px 0;
                        }
                        blockquote {
                            border-left: 4px solid #4877E8;
                            padding-left: 1em;
                            margin: 16px 0;
                            color: #666;
                            font-style: italic;
                        }
                        hr {
                            border: none;
                            border-top: 2px solid #e8eaec;
                            margin: 24px 0;
                        }
                        pre {
                            background: #f5f5f5;
                            padding: 16px;
                            border-radius: 4px;
                            font-family: 'Courier New', monospace;
                            font-size: 14px;
                            overflow-x: auto;
                            margin: 16px 0;
                            white-space: pre-wrap;
                            word-wrap: break-word;
                        }
                        code {
                            background: #f5f5f5;
                            padding: 2px 6px;
                            border-radius: 3px;
                            font-family: 'Courier New', monospace;
                            font-size: 14px;
                        }
                        pre code {
                            background: none;
                            padding: 0;
                        }
                        img {
                            max-width: 100%;
                            height: auto;
                            display: block;
                            margin: 16px auto;
                        }
                        table {
                            border-collapse: collapse;
                            width: 100%;
                            margin: 16px 0;
                        }
                        td, th {
                            border: 1px solid #000;
                            padding: 8px 12px;
                            text-align: left;
                        }
                        th {
                            background: #f6f7fc;
                            font-weight: bold;
                        }
                        @media print {
                            body {
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                        }
                    </style>
                </head>
                <body>${htmlContent}</body>
                </html>
            `)

            printDocument.close()

            // 等待内容加载后打印
            printWindow.onload = function () {
                setTimeout(() => {
                    printWindow.print()
                }, 250)
            }
        },

        saveAndBack() {
            const html = this.editor.getHTML()
            // 同时触发保存与返回，保证父组件拿到最新内容
            this.$emit('save', html)
            this.$emit('back', html)
            this.$Message.success('内容已保存并返回')
        },

        saveContent() {
            const html = this.editor.getHTML()
            this.$emit('save', html)
            this.$Message.success('内容已保存')
        },

        goBack() {
            const html = this.editor.getHTML()
            this.$emit('back', html)
        },

        // 检查内容是否需要分页
        checkPageBreak() {
            if (!this.editor) {
                console.warn('编辑器未初始化，无法执行分页')
                return
            }

            const html = this.editor.getHTML()
            console.log('执行分页，HTML内容长度:', html.length)
            this.splitContentIntoPages(html)
        },

        // 将内容分割成多页
        splitContentIntoPages(html) {
            console.log('=== 开始分页处理 ===')
            console.log('原始HTML长度:', html.length)

            // 创建临时元素来测量内容高度
            const tempDiv = document.createElement('div')
            tempDiv.style.width = '680px' // 800px - 60px*2
            tempDiv.style.padding = '60px'
            tempDiv.style.position = 'absolute'
            tempDiv.style.visibility = 'hidden'
            tempDiv.style.fontFamily = '"SimSun", "宋体", serif'
            tempDiv.style.fontSize = '16px'
            tempDiv.style.lineHeight = '1.8'
            tempDiv.innerHTML = html
            document.body.appendChild(tempDiv)

            const totalHeight = tempDiv.scrollHeight
            const maxPageHeight = this.contentHeight // 1012px

            console.log('总内容高度:', totalHeight, 'px')
            console.log('每页最大高度:', maxPageHeight, 'px')

            // 计算页数
            this.totalPages = Math.ceil(totalHeight / maxPageHeight)
            console.log('计算页数:', this.totalPages)
            this.pages = []

            // 如果没有内容或者内容很少，至少有一页
            if (!html || html.trim() === '' || totalHeight <= maxPageHeight) {
                this.pages = [{ content: html }]
                this.totalPages = 1
                console.log('内容较少，单页显示')
                document.body.removeChild(tempDiv)
                return
            }

            // 提取HTML内容并分割
            const content = html
            const parser = new DOMParser()
            const doc = parser.parseFromString(content, 'text/html')
            const children = Array.from(doc.body.children)

            console.log('子元素数量:', children.length)

            let currentPageContent = ''
            let currentHeight = 0
            let pageIndex = 1

            children.forEach((element, index) => {
                // 克隆元素来测量高度
                const clone = element.cloneNode(true)
                const measureDiv = document.createElement('div')
                measureDiv.innerHTML = clone.outerHTML
                measureDiv.style.width = '680px'
                measureDiv.style.padding = '0'
                measureDiv.style.position = 'absolute'
                measureDiv.style.visibility = 'hidden'
                measureDiv.style.fontFamily = '"SimSun", "宋体", serif'
                measureDiv.style.fontSize = '16px'
                measureDiv.style.lineHeight = '1.8'
                document.body.appendChild(measureDiv)

                const elementHeight = measureDiv.scrollHeight
                document.body.removeChild(measureDiv)

                // 如果当前页放不下，创建新页
                if (currentHeight + elementHeight > maxPageHeight && currentPageContent !== '') {
                    this.pages.push({ content: currentPageContent })
                    console.log(`第 ${pageIndex} 页完成，高度: ${currentHeight}px`)
                    currentPageContent = clone.outerHTML
                    currentHeight = elementHeight
                    pageIndex++
                } else {
                    currentPageContent += clone.outerHTML
                    currentHeight += elementHeight
                }

                // 最后一个元素
                if (index === children.length - 1 && currentPageContent !== '') {
                    this.pages.push({ content: currentPageContent })
                    console.log(`第 ${pageIndex} 页完成，高度: ${currentHeight}px`)
                }
            })

            // 更新总页数
            this.totalPages = this.pages.length

            // 清理临时元素
            document.body.removeChild(tempDiv)

            console.log(`=== 分页完成，共 ${this.totalPages} 页 ===`)
        },

        // 插入分页符
        insertPageBreak() {
            const html = `<div class="page-break-placeholder">
                <span>--- 分页符 ---</span>
            </div>`
            this.editor.chain().focus().insertContent(html).run()
            this.$Message.success('已插入分页符，打印时会在此处换页')
        },

        // 切换编辑/预览模式
        toggleEditMode() {
            this.isEditMode = !this.isEditMode

            // 切换到预览模式时，执行分页
            if (!this.isEditMode) {
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.checkPageBreak()
                        this.$Message.success(`文档已分页，共 ${this.totalPages} 页`)
                    }, 300)
                })
            }
        },

        // 暴露给父组件的方法：设置编辑器内容
        setContent(content) {
            console.log('setContent called, content length:', content?.length || 0)
            console.log('editor instance:', this.editor)
            console.log('editorReady:', this.editorReady)
            console.log('isEditMode before:', this.isEditMode)

            // 切换到编辑模式
            this.isEditMode = true

            // 等待编辑器完全初始化
            this.$nextTick(() => {
                setTimeout(() => {
                    if (this.editor && this.editorReady) {
                        console.log('Setting editor content...')

                        // 清理内容中的旧分页符
                        let cleanedContent = content.replace(/<div[^>]*class="[^"]*page-break-placeholder[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')

                        // 设置内容（不插入分页符，只在预览时才分页）
                        if (cleanedContent && cleanedContent.trim() !== '') {
                            this.editor.commands.setContent(cleanedContent)
                        } else {
                            // 如果内容为空，使用默认内容
                            this.editor.commands.setContent('<p>开始编辑您的合同内容...</p>')
                        }

                        // 确保编辑器获得焦点
                        this.editor.commands.focus()

                        console.log('Editor content set successfully')
                        console.log('Current isEditMode:', this.isEditMode)

                        // 设置内容后更新分页效果
                        setTimeout(() => {
                            this.updateEditorPageBreaks()
                        }, 300)
                    } else {
                        console.error('Editor instance not available or not ready')
                        // 重试
                        setTimeout(() => this.setContent(content), 200)
                    }
                }, 100)
            })
        },

        // 处理内容并插入分页符标记
        processContentWithPageBreaks(content) {
            if (!content) return ''

            // 创建临时DOM元素来测量内容
            const tempDiv = document.createElement('div')
            tempDiv.style.width = '680px' // 800px - 60px*2
            tempDiv.style.padding = '60px'
            tempDiv.style.position = 'absolute'
            tempDiv.style.visibility = 'hidden'
            tempDiv.innerHTML = content
            document.body.appendChild(tempDiv)

            const maxPageHeight = 1012 // 1132 - 60*2
            const elements = Array.from(tempDiv.children)
            let currentHeight = 0
            let currentPage = 1
            let resultHTML = ''

            elements.forEach((element) => {
                const clone = element.cloneNode(true)
                const measureDiv = document.createElement('div')
                measureDiv.innerHTML = clone.outerHTML
                measureDiv.style.width = '680px'
                measureDiv.style.padding = '0'
                measureDiv.style.position = 'absolute'
                measureDiv.style.visibility = 'hidden'
                document.body.appendChild(measureDiv)

                const elementHeight = measureDiv.scrollHeight
                document.body.removeChild(measureDiv)

                // 如果当前页放不下，插入分页符
                if (currentHeight + elementHeight > maxPageHeight && currentHeight > 0) {
                    currentPage++
                    resultHTML += `<div class="page-break-placeholder"><span>第 ${currentPage} 页</span></div>`
                    currentHeight = elementHeight
                } else {
                    currentHeight += elementHeight
                }

                resultHTML += clone.outerHTML
            })

            document.body.removeChild(tempDiv)

            console.log(`处理完成，共 ${currentPage} 页`)
            return resultHTML
        },

        // 检查内容并自动插入分页符（编辑模式下）
        checkAndInsertPageBreaks() {
            if (!this.editor || !this.isEditMode) return

            const html = this.editor.getHTML()

            // 创建临时元素来测量内容高度
            const tempDiv = document.createElement('div')
            tempDiv.style.width = '680px' // 800px - 60px*2
            tempDiv.style.padding = '60px'
            tempDiv.style.position = 'absolute'
            tempDiv.style.visibility = 'hidden'
            tempDiv.innerHTML = html
            document.body.appendChild(tempDiv)

            const maxPageHeight = 1012 // 1132 - 60*2
            const totalHeight = tempDiv.scrollHeight

            // 如果内容超过一页，检查是否需要插入分页符
            if (totalHeight > maxPageHeight) {
                const content = html
                const parser = new DOMParser()
                const doc = parser.parseFromString(content, 'text/html')
                const children = Array.from(doc.body.children)

                let currentHeight = 0
                let currentPage = 1
                let resultHTML = ''

                children.forEach((element) => {
                    // 克隆元素来测量高度
                    const clone = element.cloneNode(true)
                    const measureDiv = document.createElement('div')
                    measureDiv.innerHTML = clone.outerHTML
                    measureDiv.style.width = '680px'
                    measureDiv.style.padding = '0'
                    measureDiv.style.position = 'absolute'
                    measureDiv.style.visibility = 'hidden'
                    document.body.appendChild(measureDiv)

                    const elementHeight = measureDiv.scrollHeight
                    document.body.removeChild(measureDiv)

                    // 如果当前页放不下，插入分页符
                    if (currentHeight + elementHeight > maxPageHeight && currentHeight > 0) {
                        currentPage++
                        resultHTML += `<div class="page-break-placeholder"><span>第 ${currentPage} 页</span></div>`
                        currentHeight = elementHeight
                    } else {
                        currentHeight += elementHeight
                    }

                    resultHTML += clone.outerHTML
                })

                // 清理临时元素
                document.body.removeChild(tempDiv)

                // 检查HTML是否有变化（是否需要更新）
                if (html !== resultHTML) {
                    // 获取当前光标位置
                    const { from } = this.editor.state.selection

                    // 更新内容
                    this.editor.commands.setContent(resultHTML, false)

                    // 恢复光标位置
                    try {
                        this.editor.commands.focus()
                        if (from <= this.editor.state.doc.content.size) {
                            this.editor.view.dispatch(
                                this.editor.state.tr.setSelection(
                                    this.editor.state.selection.constructor.near(
                                        this.editor.state.doc.resolve(Math.min(from, this.editor.state.doc.content.size))
                                    )
                                )
                            )
                        }
                    } catch (e) {
                        // 光标恢复失败，直接聚焦到末尾
                        this.editor.chain().focus('end').run()
                    }

                    console.log(`自动插入分页符，当前共 ${currentPage} 页`)
                }
            } else {
                document.body.removeChild(tempDiv)
            }
        },

        // 更新编辑器的分页视觉效果（不实际修改内容，只更新CSS样式）
        updateEditorPageBreaks() {
            // 编辑模式不显示分页效果
            const prosemirror = document.querySelector('.ProseMirror')
            if (prosemirror) {
                prosemirror.style.backgroundImage = 'none'
            }
            this.totalPages = 1
        },

        // 暴露给父组件的方法：获取编辑器内容
        getContent() {
            if (this.editor) {
                return this.editor.getHTML()
            }
            return ''
        }
    }
}
</script>

<style scoped lang="scss">
    .editor-page {
        position: relative;
        width: 100%;
        height: 100vh;
        max-height: 100vh;
        display: flex;
        flex-direction: column;
        background: #f5f5f5;
        overflow: hidden;

        .page-header {
            height: 70px;
            min-height: 70px;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 24px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
            flex-shrink: 0;
            z-index: 1000;

        .header-left {
            display: flex;
            align-items: center;

            .back-icon {
                cursor: pointer;
                margin-right: 12px;
                color: #333;

                &:hover {
                    color: #4877E8;
                }
            }

            .header-title {
                font-size: 18px;
                font-weight: bold;
                color: #1B2742;
            }
        }

        .header-right {
            display: flex;
            align-items: center;
        }
    }

    .editor-container {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 16px;
        overflow: hidden;

        .editor-toolbar {
            background: #fff;
            border-radius: 8px 8px 0 0;
            padding: 12px 16px;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
            flex-shrink: 0;

            &.v2 {
                border-radius: 10px;
                padding: 8px 10px;
                gap: 0;
                border: 1px solid #f0f0f0;
            }

            .toolbar-group {
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }

            .toolbar-divider {
                width: 1px;
                height: 22px;
                background: #e8eaec;
                margin: 0 6px;
            }

            .toolbar-spacer {
                flex: 1;
                min-width: 12px;
            }

            .meta-text {
                font-size: 12px;
                color: #999;
                user-select: none;
            }

            .color-chip {
                font-weight: 700;
            }

            .bg-chip {
                display: inline-block;
                width: 12px;
                height: 12px;
                border-radius: 2px;
                border: 1px solid #ddd;
            }

            ::v-deep .ivu-btn {
                min-width: 34px;
                padding: 0 8px;
            }

            ::v-deep .is-active {
                color: #4877E8;
                background: #f0f5ff;
                border-color: rgba(72, 119, 232, 0.35);
            }

            /* Ribbon 布局（如截图） */
            .ribbon {
                width: 100%;
                display: flex;
                align-items: stretch;
                gap: 0;
                overflow-x: auto;
                padding-bottom: 2px;
            }

            .ribbon-section {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                padding: 6px 10px 4px;
                min-height: 64px;
                flex: 0 0 auto;

                &.wide {
                    min-width: 420px;
                }
            }

            .section-main {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 6px;
            }

            .btn-row {
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }

            .big-btn-col {
                display: flex;
                flex-direction: column;
                gap: 6px;
                align-items: center;
                justify-content: center;
            }

            .small-row {
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }

            ::v-deep .big-btn {
                width: 66px;
                height: 48px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 2px;
                padding: 0;
            }

            ::v-deep .big-btn .ivu-icon {
                font-size: 18px;
            }

            .big-btn-text {
                font-size: 12px;
                line-height: 1;
            }

            .section-title {
                margin-top: 6px;
                font-size: 12px;
                color: #6b7280;
                text-align: center;
                user-select: none;
                line-height: 1;
            }

            .ribbon-divider {
                width: 1px;
                background: #e8eaec;
                margin: 6px 2px;
            }

            .ribbon-spacer {
                flex: 1;
                min-width: 10px;
            }

            .word-pill {
                height: 28px;
                display: inline-flex;
                align-items: center;
                padding: 0 10px;
                border-radius: 999px;
                background: #f6f7fc;
                color: #545F78;
                font-size: 12px;
                user-select: none;
                border: 1px solid #e8eaec;
            }

            .with-caret {
                position: relative;
                padding-right: 22px !important;
            }

            .with-caret .caret {
                position: absolute;
                right: 6px;
                top: 50%;
                transform: translateY(-50%);
                font-size: 14px;
                opacity: 0.65;
            }

            .find-box {
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }

            .find-count {
                font-size: 12px;
                color: #6b7280;
                user-select: none;
                margin-left: 4px;
                min-width: 42px;
                text-align: right;
            }
        }

        .editor-content {
            flex: 1;
            background: #e8eaed;
            border-radius: 0 0 8px 8px;
            overflow-y: auto;
            overflow-x: hidden;
            padding: 20px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

            // 编辑模式样式
            .editor-mode {
                .pages-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 24px;
                    padding-bottom: 40px;

                    .page-editor-wrapper {
                        width: 800px;
                        background: #fff;
                        border-radius: 2px;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    }

                    .editor-wrapper {
                        ::v-deep .ProseMirror {
                            width: 800px;
                            min-height: 1132px;
                            height: auto; // 允许编辑器自动扩展
                            background: #fff;
                            padding: 60px;
                            border-radius: 2px;
                            font-family: "SimSun", "宋体", serif;
                            font-size: 16px;
                            line-height: 1.8;
                            color: #333;
                            outline: none;
                            -webkit-font-smoothing: antialiased;
                            -moz-osx-font-smoothing: grayscale;

                            // 动态背景（通过JS计算分页位置）
                            background-attachment: local;
                            background-repeat: no-repeat;
                            background-size: 100% 100%;

                            // 添加分页符样式，让内容在视觉上分页
                            .page-break-placeholder {
                                width: 100%;
                                height: 60px;
                                margin: 30px 0;
                                border-bottom: 2px dashed #4877E8;
                                position: relative;
                                text-align: center;
                                background: linear-gradient(to bottom, transparent 50%, #f0f5ff 50%, #f0f5ff calc(50% + 1px), transparent calc(50% + 1px));

                                &::before {
                                    content: '--- 第 ' attr(data-page) ' 页 ---';
                                    position: absolute;
                                    top: 50%;
                                    left: 50%;
                                    transform: translate(-50%, -50%);
                                    font-size: 12px;
                                    color: #4877E8;
                                    background: #fff;
                                    padding: 4px 12px;
                                    border-radius: 4px;
                                    white-space: nowrap;
                                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                                }
                            }

                            >*:first-child {
                                margin-top: 0;
                            }

                            >*:last-child {
                                margin-bottom: 0;
                            }

                            // 占位符样式
                            p.is-editor-empty:first-child::before {
                                content: attr(data-placeholder);
                                float: left;
                                color: #adb5bd;
                                pointer-events: none;
                                height: 0;
                            }

                            // 焦点样式
                            &:focus {
                                outline: none;
                            }

                            // 选中状态样式
                            &::selection {
                                background: rgba(72, 119, 232, 0.2);
                            }

                            h1 {
                                font-size: 28px;
                                font-weight: bold;
                                margin: 20px 0;
                                color: #1B2742;
                            }

                            h2 {
                                font-size: 24px;
                                font-weight: bold;
                                margin: 18px 0;
                                color: #1B2742;
                            }

                            h3 {
                                font-size: 20px;
                                font-weight: bold;
                                margin: 16px 0;
                                color: #1B2742;
                            }

                            h4 {
                                font-size: 18px;
                                font-weight: bold;
                                margin: 14px 0;
                                color: #1B2742;
                            }

                            p {
                                margin: 12px 0;
                                text-indent: 2em;
                            }

                            ul,
                            ol {
                                margin: 12px 0;
                                padding-left: 2em;
                            }

                            li {
                                margin: 6px 0;
                            }

                            blockquote {
                                border-left: 4px solid #4877E8;
                                padding-left: 1em;
                                margin: 16px 0;
                                color: #666;
                                font-style: italic;
                            }

                            hr {
                                border: none;
                                border-top: 2px solid #e8eaec;
                                margin: 24px 0;
                            }

                            pre {
                                background: #f5f5f5;
                                padding: 16px;
                                border-radius: 4px;
                                font-family: 'Courier New', monospace;
                                font-size: 14px;
                                overflow-x: auto;
                                margin: 16px 0;
                            }

                            code {
                                background: #f5f5f5;
                                padding: 2px 6px;
                                border-radius: 3px;
                                font-family: 'Courier New', monospace;
                                font-size: 14px;
                            }

                            pre code {
                                background: none;
                                padding: 0;
                            }

                            img {
                                max-width: 100%;
                                height: auto;
                                display: block;
                                margin: 16px auto;
                            }

                            table {
                                border-collapse: collapse;
                                width: 100%;
                                margin: 16px 0;
                            }

                            td,
                            th {
                                border: 1px solid #e8eaec;
                                padding: 8px 12px;
                                text-align: left;
                            }

                            th {
                                background: #f6f7fc;
                                font-weight: bold;
                            }
                        }
                    }
                }

                .page-counter {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 16px 0;

                    .counter-content {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        padding: 10px 24px;
                        background: linear-gradient(135deg, #4877E8 0%, #6a9fff 100%);
                        border-radius: 24px;
                        box-shadow: 0 4px 12px rgba(72, 119, 232, 0.3);
                        font-size: 14px;
                        color: #fff;
                        font-weight: 500;

                        .ivu-icon {
                            margin-right: 8px;
                            font-size: 18px;
                            color: #fff;
                        }

                        .counter-text {
                            color: #fff;
                            margin: 0 6px;
                        }

                        .divider {
                            color: rgba(255, 255, 255, 0.5);
                            margin: 0 10px;
                        }
                    }
                }
            }

            // 预览模式样式
            .preview-mode {
                .pages-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 24px;
                    padding-bottom: 40px;

                    .page {
                        width: 800px;
                        min-height: 1132px;
                        background: #fff;
                        padding: 60px;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        position: relative;
                        border-radius: 2px;
                        cursor: pointer;
                        transition: all 0.3s ease;

                        &:hover {
                            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
                            transform: translateY(-2px);
                        }

                        &.active-page {
                            box-shadow: 0 0 0 3px #4877E8, 0 4px 12px rgba(72, 119, 232, 0.3);
                        }

                        .page-number {
                            position: absolute;
                            top: 15px;
                            right: 20px;
                            font-size: 11px;
                            color: #999;
                            font-family: Arial, sans-serif;
                        }

                        .page-content {
                            font-family: "SimSun", "宋体", serif;
                            font-size: 16px;
                            line-height: 1.8;
                            color: #333;

                            h1 {
                                font-size: 28px;
                                font-weight: bold;
                                margin: 20px 0;
                                color: #1B2742;
                            }

                            h2 {
                                font-size: 24px;
                                font-weight: bold;
                                margin: 18px 0;
                                color: #1B2742;
                            }

                            h3 {
                                font-size: 20px;
                                font-weight: bold;
                                margin: 16px 0;
                                color: #1B2742;
                            }

                            h4 {
                                font-size: 18px;
                                font-weight: bold;
                                margin: 14px 0;
                                color: #1B2742;
                            }

                            p {
                                margin: 12px 0;
                                text-indent: 2em;
                            }

                            ul,
                            ol {
                                margin: 12px 0;
                                padding-left: 2em;
                            }

                            li {
                                margin: 6px 0;
                            }

                            blockquote {
                                border-left: 4px solid #4877E8;
                                padding-left: 1em;
                                margin: 16px 0;
                                color: #666;
                                font-style: italic;
                            }

                            hr {
                                border: none;
                                border-top: 2px solid #e8eaec;
                                margin: 24px 0;
                            }

                            pre {
                                background: #f5f5f5;
                                padding: 16px;
                                border-radius: 4px;
                                font-family: 'Courier New', monospace;
                                font-size: 14px;
                                overflow-x: auto;
                                margin: 16px 0;
                            }

                            code {
                                background: #f5f5f5;
                                padding: 2px 6px;
                                border-radius: 3px;
                                font-family: 'Courier New', monospace;
                                font-size: 14px;
                            }

                            pre code {
                                background: none;
                                padding: 0;
                            }

                            img {
                                max-width: 100%;
                                height: auto;
                                display: block;
                                margin: 16px auto;
                            }

                            table {
                                border-collapse: collapse;
                                width: 100%;
                                margin: 16px 0;
                            }

                            td,
                            th {
                                border: 1px solid #e8eaec;
                                padding: 8px 12px;
                                text-align: left;
                            }

                            th {
                                background: #f6f7fc;
                                font-weight: bold;
                            }
                        }
                    }

                    .page-placeholder {
                        width: 800px;
                        height: 1132px;
                        background: #fff;
                        border: 2px dashed #d0d0d0;
                        border-radius: 2px;
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        .placeholder-text {
                            text-align: center;
                            color: #999;

                            .ivu-icon {
                                margin-bottom: 12px;
                                color: #ccc;
                            }

                            p {
                                margin: 0;
                                font-size: 16px;
                            }
                        }
                    }

                    .page-counter {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        padding: 16px 0;

                        .counter-content {
                            display: inline-flex;
                            align-items: center;
                            justify-content: center;
                            padding: 10px 24px;
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                            border-radius: 24px;
                            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
                            font-size: 14px;
                            color: #fff;
                            font-weight: 500;

                            .ivu-icon {
                                margin-right: 8px;
                                font-size: 18px;
                                color: #fff;
                            }

                            .counter-text {
                                color: #fff;
                                margin: 0 6px;
                            }

                            .divider {
                                color: rgba(255, 255, 255, 0.5);
                                margin: 0 10px;
                            }
                        }
                    }
                }
            }
        }
    }
}

// 打印样式 - A4 纸张尺寸
@media print {
    .editor-page {
        .page-header {
            display: none !important;
        }

        .editor-container {
            padding: 0 !important;

            .editor-toolbar {
                display: none !important;
            }

            .editor-content {
                padding: 0 !important;
                background: #fff !important;

                .editor-mode {
                    display: none !important;
                }

                .preview-mode {
                    ::v-deep .page-counter {
                        display: none !important;
                    }
                }
            }
        }
    }
}

// 全局样式 - 移除输入框黑边框
::v-deep .ivu-input {
    border-color: #dcdee2 !important;

    &:focus {
        border-color: #4877E8 !important;
        box-shadow: 0 0 0 2px rgba(72, 119, 232, 0.1) !important;
    }

    &:hover {
        border-color: #c5c8ce !important;
    }
}

::v-deep .ivu-input-number {
    border-color: #dcdee2 !important;

    &:hover {
        border-color: #c5c8ce !important;
    }

    &:focus-within {
        border-color: #4877E8 !important;
        box-shadow: 0 0 0 2px rgba(72, 119, 232, 0.1) !important;
    }

    .ivu-input-number-input {
        border: none !important;
    }
}

::v-deep .ivu-select {
    .ivu-select-selection {
        border-color: #dcdee2 !important;

        &:hover {
            border-color: #c5c8ce !important;
        }
    }

    &.ivu-select-visible .ivu-select-selection {
        border-color: #4877E8 !important;
        box-shadow: 0 0 0 2px rgba(72, 119, 232, 0.1) !important;
    }
}

// 优化 Modal 样式
::v-deep .ivu-modal {
    .ivu-modal-header {
        border-bottom: 1px solid #e8eaec;
        padding: 16px 24px;
    }

    .ivu-modal-footer {
        border-top: 1px solid #e8eaec;
        padding: 16px 24px;
    }
}

// 优化 FormItem 标签样式
::v-deep .ivu-form-item-label {
    font-weight: 500;
    color: #333;
}

// Tiptap Floating Menu 样式
.tiptap-floating-menu {
    display: flex;
    background: #fff;
    border: 1px solid #e8eaec;
    border-radius: 6px;
    padding: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;

    button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border: none;
        background: transparent;
        cursor: pointer;
        border-radius: 4px;
        color: #515a6e;
        font-size: 14px;
        transition: all 0.2s;
        margin: 0 2px;

        &:hover {
            background: #f0f5ff;
            color: #2d8cf0;
        }

        &.is-active {
            background: #e6f7ff;
            color: #2d8cf0;
        }

        strong,
        em,
        s {
            font-style: normal;
            text-decoration: none;
        }

        strong {
            font-weight: bold;
        }

        em {
            font-style: italic;
        }

        s {
            text-decoration: line-through;
        }
    }

    .divider {
        width: 1px;
        height: 24px;
        background: #e8eaec;
        margin: 6px 4px;
    }
}
</style>
